import React, { useState } from 'react';
import type { CustomDrawerProps } from './TablemaintPage';
import TablemaintDrawer from './TablemaintDrawer';
import tablemaintService from '../../services/tablemaint.service';
import { findTable } from '../../config/dataTables.registry';

/**
 * Quote Charge Mapping drawer.
 *
 * The grid lists EVERY active quote charge (list_sql), so a clicked row isn't always
 * a mapping record: recno > 0 is a real invoice_litype_addlcharges row — edit it with
 * the generic drawer as-is. recno 0 is an unmapped charge — open the generic drawer
 * in CREATE mode with the Quote Charge pinned via presetValues (rendered read-only),
 * so mapping a charge from the grid is one click + one select. The + button still
 * creates from scratch (row null → plain create, charge select enabled).
 *
 * Un-mapping is the "Remove Mapping" button (renderPanels), not the engine's
 * Active/Deleted toggle (cfg.noSoftDelete hides it): "deleted mapping" and "not
 * mapped" are the same thing to this grid, and the engine reactivates the
 * soft-deleted row if the charge is ever mapped again (dup-guard).
 */
const QuoteChargeMappingEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const cfg = findTable('invoice-litype-charge-map')!;
  const mappingRecno = Number(row?.recno ?? 0);
  const isPinnedCreate = !!row && mappingRecno <= 0;
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleRemove = async (recno: number | string) => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await tablemaintService.remove(cfg.key, recno);
      await onSaved();
      onClose();
    } catch {
      setRemoveError('Failed to remove the mapping. Please try again.');
      setRemoving(false);
    }
  };

  return (
    <TablemaintDrawer
      cfg={cfg}
      isOpen
      row={mappingRecno > 0 ? row : null}
      {...(isPinnedCreate
        ? { presetValues: { additionalcharge_recno: String(row.additionalcharge_recno ?? '0') } }
        : {})}
      renderPanels={(r) => {
        const recno = Number(r.recno ?? 0);
        if (recno <= 0) return null;
        return (
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={removing}
              onClick={() => handleRemove(recno)}
              className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              {removing ? 'Removing…' : 'Remove Mapping'}
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              The charge goes back to billing under its own description.
            </span>
            {removeError && (
              <span className="text-xs font-medium text-red-600 dark:text-red-400">{removeError}</span>
            )}
          </div>
        );
      }}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
};

export default QuoteChargeMappingEditDrawer;
